import FeedDetail from '../../components/Feed/FeedDetail';
import type { IFeedItem } from '../../components/FeedItem/FeedItem';
import { getReportDetail } from '../../features/queries/getReportDetail';
import { entityId } from '../../lib/ids';
import { useQ } from '../../lib/query';
import { __ } from '../../utils';
import { Box, Drawer, Skeleton, Stack } from '@presov.sk/presov-ui-react';
import React from 'react';

/** Šírka panela; na úzkom okne ustúpi obrazovke. */
const DRAWER_WIDTH = 460;

type Props = {
  /** Krátke ID z adresy (`?detail=MQRJ73F`), nie appková podoba. */
  shortReportId: string;
  onClose: () => void;
};

/**
 * Detail vlastného podnetu **vysunutý sprava nad zoznamom**.
 *
 * Predtým sa klik do riadku vybral na mapu (`/podnety/:id` → `/?detail=`)
 * a bolo to kontraproduktívne: občan chce v zozname svojich podnetov vidieť,
 * čo mu úrad odpísal, a vrátiť sa o riadok nižšie - nie odísť na hlavnú
 * stránku a hľadať cestu späť. Mapa zostáva tam, kde dáva zmysel: pri
 * verejných podnetoch a pri odkazoch z notifikácií.
 *
 * Je to `Drawer`, nie stĺpec v `setAside`. Stĺpec by zoznamu natrvalo zobral
 * 460 px a tabuľka pod ním má šesť stĺpcov, ktoré sa už raz krátili; detail je
 * navyše dočasný pohľad, nie druhá polovica obrazovky. Vrstva nad zoznamom
 * naviac znesie aj úzke okno, kde by sa tretí stĺpec nezmestil vôbec.
 *
 * Obsah je **ten istý** `FeedDetail`, aký kreslí panel nad mapou aj mobilný
 * sheet; líši sa len tým, odkiaľ berie podnet. Panel na mape ho má z feedu, tu
 * sa doťahuje: v zozname môže byť riadok na inej strane a odkaz sa dá otvoriť
 * aj priamo.
 */
const ReportDetailDrawer: React.FC<Props> = ({ shortReportId, onClose }) => {
  const reportId = entityId(shortReportId, 'report');

  const { data: report, isLoading } = useQ(getReportDetail, { reportId });

  const item = React.useMemo<IFeedItem | undefined>(() => {
    if (!report) return undefined;

    return {
      // Zoznam je „moje podnety", takže vlastníctvo je isté - a `FeedDetail`
      // z neho vie, že podporiť sa vlastný podnet nedá.
      type: 'userReport',
      id: report.id,
      title: report.title,
      description: report.description ?? '',
      date: report.inserted_at,
      media: report.media.map((media) => ({
        url: media.public_url,
        type: media.media_type,
        filename: media.filename,
      })),
      location: report.location as {
        type: 'Point';
        coordinates: [number, number];
      },
      votesCount: report.votes_count ?? 0,
      status: report.status,
    };
  }, [report]);

  return (
    <Drawer open anchor='right' onClose={onClose}>
      <Box
        sx={{
          width: { xs: '100vw', sm: DRAWER_WIDTH },
          maxWidth: '100vw',
          height: '100%',
          p: 1.5,
        }}
      >
        {isLoading || !item ? (
          <Stack sx={{ gap: 1.25 }}>
            <Skeleton variant='rounded' width='100%' height={132} />
            <Skeleton variant='rounded' width='60%' height={24} />
            <Skeleton variant='rounded' width='100%' height={72} />
          </Stack>
        ) : (
          // Geokóder tu nemá pohľad mapy - `reverseGeocode` si vtedy vyrobí
          // vlastný view model, takže adresa v riadku pod fotkami je aj tu.
          <FeedDetail
            item={item}
            onBack={onClose}
            backLabel={__('Späť na zoznam')}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default ReportDetailDrawer;
